/**
 * Interactive Living Technical Whitepaper.
 *
 * System Architecture, Semantic Context Compression, and Empirical Verification of Archis.
 * Built for senior engineers, tech leads, compiler specialists, and security evaluators.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'

import { LANGUAGES } from '@/entities/languages'
import {
  AGENT_LOOP,
  BENCHMARK_NOTE,
  CLI_VERSION,
  COHORT5,
  COHORT5_BUILDS,
  COHORT5_GATES,
  FASE_C_COHORT5,
  FASE_C_LANGS,
  FIDELITY,
  INDEXING,
  INDEXING_GATE,
  PUBLIC_COHORT5,
  REPRESENTATION,
  RUNTIME,
  SUPPORTING_PUBLIC,
  TASK_CONTEXT,
  TASK_CONTEXT_BAND,
  BENCHMARK_REPO,
  benchmarkFile,
} from '@/entities/metrics'
import { stackLabels } from '@/shared/lib/stackTable'
import { Footer, Masthead } from '@/shared/ui/Chrome'
import { Badge, Label } from '@/shared/ui/primitives'
import { CompilePipeline, FlowMap, PrivacyBoundary } from '@/shared/ui/walkthrough'

export function Whitepaper() {
  const [activeBenchTab, setActiveBenchTab] = useState<'cost' | 'repos' | 'langs' | 'repair' | 'fidelity'>('cost')

  return (
    <>
      <Masthead />
      <main id="main" className="whitepaper-page">
        {/* Document Header */}
        <header className="shell" style={{ paddingBlock: 'var(--s8) var(--s6)' }}>
          <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
            <div className="row" style={{ gap: 'var(--s3)' }}>
              <Label index="SPEC-01">Technical Whitepaper & Architecture</Label>
              <Badge>CLI {CLI_VERSION}</Badge>
              <Badge>{COHORT5_BUILDS.repos}-repo indexing gate</Badge>
              <Badge>{LANGUAGES.length} languages</Badge>
            </div>

            <h1 style={{ fontSize: 'clamp(2.2rem, 4.8vw, 3.6rem)', lineHeight: 1.15, maxWidth: '36ch' }}>
              High-Fidelity Semantic Context Compression & Knowledge Graphs for Autonomous Coding Agents
            </h1>

            <p className="lead" style={{ maxWidth: '72ch' }}>
              A technical specification and empirical evaluation of local AST-based semantic compilation,
              BLAKE3 Merkle-tree incremental invalidation, reversible runtime output compression, and
              cryptographically verifiable offline licensing.
            </p>

            <div className="row mono-note" style={{ gap: 'var(--s4)', borderTop: '1px solid var(--rule)', paddingTop: 'var(--s3)', flexWrap: 'wrap' }}>
              <span>Published: August 2026</span>
              <span className="muted">·</span>
              <span>Classification: Architecture & Verification</span>
              <span className="muted">·</span>
              <span>Target: Deterministic Local Execution</span>
            </div>
          </div>
        </header>

        {/* Table of Contents & Navigation */}
        <section className="shell" style={{ paddingBottom: 'var(--s6)' }}>
          <div className="panel panel--signal">
            <div className="stack" style={{ ['--gap' as string]: 'var(--s3)' }}>
              <h4 style={{ fontFamily: 'var(--display)', margin: 0 }}>Contents & Sections</h4>
              <div className="grid-3" style={{ gap: 'var(--s2) var(--s4)', fontSize: '0.875rem' }}>
                <a href="#abstract" className="signal">§ 0. Abstract & Executive Summary</a>
                <a href="#problem" className="signal">§ 1. The Context Bottleneck in LLM Agents</a>
                <a href="#pipeline" className="signal">§ 2. 5-Stage Semantic Compiler Pipeline</a>
                <a href="#invalidation" className="signal">§ 3. Content Identity & Merkle Invalidation</a>
                <a href="#runtime" className="signal">§ 4. Reversible Runtime Output Compression</a>
                <a href="#benchmarks" className="signal">§ 5. Empirical Benchmarks (indexing + task-context)</a>
                <a href="#agent-loop" className="signal">§ 6. End-to-End LLM Agent Execution</a>
                <a href="#security" className="signal">§ 7. Cryptographic Safety & Offline Boundary</a>
                <a href="#conclusion" className="signal">§ 8. Reproduce it yourself</a>
              </div>
            </div>
          </div>
        </section>

        {/* Section 0: Abstract */}
        <section className="shell" id="abstract" style={{ paddingBottom: 'var(--s8)', scrollMarginTop: '5rem' }}>
          <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
            <div className="stack" style={{ ['--gap' as string]: 'var(--s2)' }}>
              <Label index="00">Abstract</Label>
              <h2>Executive Summary</h2>
            </div>
            <div className="glass stack" style={{ ['--gap' as string]: 'var(--s3)', fontStyle: 'italic', padding: 'var(--s6)' }}>
              <p style={{ margin: 0 }}>
                Autonomous coding agents (e.g., Claude Code, OpenAI Codex CLI, Gemini CLI) frequently suffer from
                context-window saturation, high API token costs, and attention degradation caused by naive file-dumping
                and unconstrained terminal output ingestion. While classical text search (grep) lacks structural semantics
                and dense vector embeddings fail to resolve exact declaration hierarchies or compute blast radius,
                Archis introduces a local, deterministic, one-way <strong>semantic compiler</strong> coupled with an
                in-memory SQLite knowledge graph and an MCP server interface.
              </p>
              <p style={{ margin: 0 }}>
                By lowering {LANGUAGES.length} programming languages into a canonical intermediate representation (<code>chi.core.v1</code>)
                and projecting into a compact structural form (<code>compact.v1</code>), the published cohort-5 run on{' '}
                {COHORT5_BUILDS.build} indexes <strong>{INDEXING_GATE.repos} pinned public repositories</strong> and{' '}
                <strong>{INDEXING_GATE.files} files</strong>; <strong>{INDEXING_GATE.deterministic}</strong> produce the same graph hash on
                every repetition, and they measure a <strong>median {PUBLIC_COHORT5.medianReduction}% smaller
                representation</strong> than source. Every row is public in{' '}
                <a href={benchmarkFile(PUBLIC_COHORT5.path)} target="_blank" rel="noreferrer">
                  {BENCHMARK_REPO.name} <code>{PUBLIC_COHORT5.path}</code>
                </a>
                , and the figures here are computed from those files at the tag <code>{COHORT5_BUILDS.tag}</code>. Separately, a 20-repository agent-task
                band measures <strong>{TASK_CONTEXT_BAND.value}{TASK_CONTEXT_BAND.unit} less task context</strong> versus a
                full-source agent — that range is not pooled with the indexing median. Incremental re-indexing powered by
                BLAKE3 Merkle roots updates a changed file <strong>3.0× to 14.7× faster</strong> than a full rebuild on the
                five repositories in §03. Reversible tool output compression shortens terminal and test logs while keeping
                every original hash-verified and recoverable over local MCP. There is no source endpoint.
              </p>
            </div>
          </div>
        </section>

        {/* Section 1: The Problem */}
        <section className="shell" id="problem" style={{ paddingBottom: 'var(--s8)', scrollMarginTop: '5rem' }}>
          <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
            <div className="stack" style={{ ['--gap' as string]: 'var(--s2)' }}>
              <Label index="01">Formal Problem Statement</Label>
              <h2>The Context Bottleneck in LLM Agents</h2>
              <p className="dim" style={{ maxWidth: '72ch', margin: 0 }}>
                Contemporary LLM agents execute tool-use loops to understand codebases. When an agent attempts to localize
                and modify functionality in a project exceeding 10,000 lines of code, it encounters three fundamental pathologies:
              </p>
            </div>

            <div className="grid-3" style={{ gap: 'var(--s5)' }}>
              <div className="card stack" style={{ ['--gap' as string]: 'var(--s3)', padding: 'var(--s5)' }}>
                <h4 style={{ fontFamily: 'var(--display)', fontSize: '1.15rem', margin: 0, color: 'var(--text)' }}>
                  1. Over-Exposure of Source
                </h4>
                <p className="dim" style={{ fontSize: '0.9375rem', lineHeight: 1.6, margin: 0 }}>
                  Reading an entire 1,500-line source file to inspect a single helper function introduces thousands of
                  irrelevant tokens into the attention window. This induces the <em>“Lost in the Middle”</em> phenomenon
                  and inflates input token costs on every subsequent turn (<em>O(N · K)</em> cost scaling).
                </p>
              </div>

              <div className="card stack" style={{ ['--gap' as string]: 'var(--s3)', padding: 'var(--s5)' }}>
                <h4 style={{ fontFamily: 'var(--display)', fontSize: '1.15rem', margin: 0, color: 'var(--text)' }}>
                  2. Blind Retrieval
                </h4>
                <p className="dim" style={{ fontSize: '0.9375rem', lineHeight: 1.6, margin: 0 }}>
                  String matching (<code>grep</code>) returns lines without syntactic scope or call hierarchies.
                  Vector search (embeddings) operates on statistical token similarity, failing on exact identifier
                  resolution, transitive caller graphs, and structural type hierarchies.
                </p>
              </div>

              <div className="card stack" style={{ ['--gap' as string]: 'var(--s3)', padding: 'var(--s5)' }}>
                <h4 style={{ fontFamily: 'var(--display)', fontSize: '1.15rem', margin: 0, color: 'var(--text)' }}>
                  3. Session Amnesia
                </h4>
                <p className="dim" style={{ fontSize: '0.9375rem', lineHeight: 1.6, margin: 0 }}>
                  Standard agent harnesses maintain zero persistent index between turns. Each new subtask forces the agent
                  to re-traverse the file system, re-parse dependencies, and re-read previously seen headers from scratch.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Pipeline */}
        <section className="shell" id="pipeline" style={{ paddingBottom: 'var(--s8)', scrollMarginTop: '5rem' }}>
          <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
            <div className="stack" style={{ ['--gap' as string]: 'var(--s3)' }}>
              <Label index="02">Compiler Architecture</Label>
              <h2>The 5-Stage Semantic Compiler Pipeline</h2>
              <p className="lead" style={{ maxWidth: '72ch' }}>
                Archis operates as a deterministic, single-direction semantic compiler. Source code is parsed via tree-sitter,
                lowered to a canonical intermediate representation, and projected into a token-optimal format.
              </p>
            </div>

            <CompilePipeline />

            <div className="stack" style={{ ['--gap' as string]: 'var(--s3)' }}>
              <h3 style={{ fontFamily: 'var(--display)' }}>End-to-End Call Execution Path</h3>
              <p className="dim" style={{ maxWidth: '72ch', margin: 0 }}>
                The agent communicates with Archis over local Model Context Protocol (MCP stdio/loopback).
                Queries resolve against the local SQLite graph with zero network transit.
              </p>
              <FlowMap />
            </div>
          </div>
        </section>

        {/* Section 3: Invalidation */}
        <section className="shell" id="invalidation" style={{ paddingBottom: 'var(--s8)', scrollMarginTop: '5rem' }}>
          <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
            <div className="stack" style={{ ['--gap' as string]: 'var(--s2)' }}>
              <Label index="03">Incremental Invalidation</Label>
              <h2>Content Identity &amp; BLAKE3 Merkle Trees (CAVS DNA)</h2>
              <p className="dim" style={{ maxWidth: '72ch', margin: 0 }}>
                Archis inherits its content-addressing core from <strong>CAVS</strong> (<em>Content-Addressable Verified Streaming</em>).
                Every source file, syntax node, and IR projection is indexed using <strong>BLAKE3 cryptographic hashes organized
                into Merkle trees</strong>.
              </p>
            </div>

            <div className="table-wrap">
              <table className="table table--stack" style={stackLabels(['Language', 'Clean rebuild', 'One file changed', 'Speedup'])}>
                <thead>
                  <tr>
                    <th>Repository</th>
                    <th>Language</th>
                    <th className="num">Clean Rebuild</th>
                    <th className="num">Single-File Update</th>
                    <th className="num">Speedup Ratio</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['guava', 'Java', '4,280 ms', '291 ms', '14.7×'],
                    ['spring-boot', 'Java', '37,120 ms', '3,410 ms', '10.9×'],
                    ['angular', 'TypeScript', '18,400 ms', '1,920 ms', '9.6×'],
                    ['gin', 'Go', '412 ms', '137 ms', '3.0×'],
                    ['ripgrep', 'Rust', '820 ms', '195 ms', '4.2×'],
                  ].map(([repo, lang, clean, inc, speed]) => (
                    <tr key={repo}>
                      <td>{repo}</td>
                      <td className="mono-note">{lang}</td>
                      <td className="num dim">{clean}</td>
                      <td className="num signal">{inc}</td>
                      <td className="num" style={{ fontWeight: 600 }}>{speed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="mono-note">
              On 21 probed test corpora, the graph produced by an incremental update is byte-identical (canonical hash match)
              to a clean build from scratch.
            </p>
          </div>
        </section>

        {/* Section 4: Runtime */}
        <section className="shell" id="runtime" style={{ paddingBottom: 'var(--s8)', scrollMarginTop: '5rem' }}>
          <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
            <div className="stack" style={{ ['--gap' as string]: 'var(--s2)' }}>
              <Label index="04">Tool Output Management</Label>
              <h2>Reversible Runtime Output Compression</h2>
              <p className="dim" style={{ maxWidth: '72ch', margin: 0 }}>
                An agent's context is consumed not only by repository files, but by test suites, compilation errors, and
                linter outputs. Archis applies structural pattern extractors to command stdout/stderr:
              </p>
            </div>

            <div className="table-wrap">
              <table className="table table--stack" style={stackLabels(['Before', 'After'])}>
                <thead>
                  <tr>
                    <th>Signal / Fixture</th>
                    <th className="num">Before (Raw Output)</th>
                    <th className="num">With Archis</th>
                  </tr>
                </thead>
                <tbody>
                  {RUNTIME.map((row) => (
                    <tr key={row.signal} title={row.source}>
                      <td>
                        <div>{row.signal}</div>
                        <div className="dim" style={{ fontSize: '0.8125rem', marginTop: 'var(--s1)' }}>
                          {row.detail}
                        </div>
                      </td>
                      <td className="num muted">{row.before}</td>
                      <td className="num signal">{row.after}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="panel">
              <h4 style={{ fontFamily: 'var(--display)' }}>Guaranteed Reversibility via <code>local://</code> Handles</h4>
              <p className="dim" style={{ marginTop: 'var(--s2)', fontSize: '0.9375rem' }}>
                When Archis truncates or summarizes a log, the raw bytes are journaled into a local content-addressed
                artifact store. The MCP tool output carries a <code>local://&lt;sha256&gt;</code> reference. If the LLM
                requires full stack trace context, it queries <code>chi tools artifact get &lt;uri&gt;</code> over MCP without
                requiring a server round-trip.
              </p>
            </div>
          </div>
        </section>

        {/* Section 5: Empirical Benchmarks */}
        <section className="shell" id="benchmarks" style={{ paddingBottom: 'var(--s8)', scrollMarginTop: '5rem' }}>
          <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
            <div className="stack" style={{ ['--gap' as string]: 'var(--s3)' }}>
              <Label index="05">Empirical Evaluation</Label>
              <h2>Indexing gate, then the agent-task band</h2>
              <p className="lead" style={{ maxWidth: '72ch' }}>
                Two campaigns, never one suite. Cohort 5 is the {COHORT5_BUILDS.repos}-repo indexing gate. Task-context is 20
                repos with a labeled 89.4–95% policy band. The gate figures are computed from the public run tagged{' '}
                <code>{COHORT5_BUILDS.tag}</code>, measured on {COHORT5_BUILDS.build}. CLI {CLI_VERSION} is the shipping build; it is not a
                claim that the gate was re-measured on it. Token counts use <code>tiktoken</code> (cl100k_base / o200k_base).
              </p>
            </div>

            <div className="metric-band">
              {INDEXING.map((metric) => (
                <div className="metric" key={metric.meaning} title={metric.source}>
                  <div className="metric__value">
                    {metric.value}
                    {metric.unit && <span className="metric__unit">{metric.unit}</span>}
                  </div>
                  <div className="metric__meaning">{metric.meaning}</div>
                </div>
              ))}
            </div>

            <div className="card stack" style={{ ['--gap' as string]: 'var(--s2)', padding: 'var(--s5)' }} title={TASK_CONTEXT_BAND.source}>
              <div className="mono-note">Agent-task band · 20 repositories · not pooled with indexing</div>
              <div className="row" style={{ gap: 'var(--s4)', alignItems: 'baseline', flexWrap: 'wrap' }}>
                <div className="metric__value" style={{ fontSize: '2rem', fontFamily: 'var(--mono)', color: 'var(--signal)' }}>
                  {TASK_CONTEXT_BAND.value}
                  {TASK_CONTEXT_BAND.unit && <span className="metric__unit">{TASK_CONTEXT_BAND.unit}</span>}
                </div>
                <p className="dim" style={{ margin: 0, maxWidth: '56ch' }}>{TASK_CONTEXT_BAND.meaning}</p>
              </div>
            </div>

            <div className="metric-band">
              {SUPPORTING_PUBLIC.map((metric) => (
                <div className="metric" key={metric.meaning} title={metric.source}>
                  <div className="metric__value">
                    {metric.value}
                    {metric.unit && <span className="metric__unit">{metric.unit}</span>}
                  </div>
                  <div className="metric__meaning">{metric.meaning}</div>
                </div>
              ))}
            </div>

            {/* Tab Navigation */}
            <div className="tabs" role="tablist" aria-label="Benchmark tabs">
              <button
                type="button"
                className="tab"
                role="tab"
                aria-selected={activeBenchTab === 'cost'}
                onClick={() => setActiveBenchTab('cost')}
              >
                Cost Per Task ({TASK_CONTEXT.length})
              </button>
              <button
                type="button"
                className="tab"
                role="tab"
                aria-selected={activeBenchTab === 'repos'}
                onClick={() => setActiveBenchTab('repos')}
              >
                All Repositories ({REPRESENTATION.length})
              </button>
              <button
                type="button"
                className="tab"
                role="tab"
                aria-selected={activeBenchTab === 'langs'}
                onClick={() => setActiveBenchTab('langs')}
              >
                Per-Language Extraction ({COHORT5.length})
              </button>
              <button
                type="button"
                className="tab"
                role="tab"
                aria-selected={activeBenchTab === 'repair'}
                onClick={() => setActiveBenchTab('repair')}
              >
                Mutation Bug Repair ({FASE_C_LANGS.length})
              </button>
              <button
                type="button"
                className="tab"
                role="tab"
                aria-selected={activeBenchTab === 'fidelity'}
                onClick={() => setActiveBenchTab('fidelity')}
              >
                Fidelity & Gates ({FIDELITY.length + COHORT5_GATES.length})
              </button>
            </div>

            {/* Tab Content */}
            <div className="glass" style={{ padding: 'var(--s5)' }}>
              {activeBenchTab === 'cost' && (
                <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
                  <h4>Cost per Task: agent-task band (20 repos, not pooled with indexing)</h4>
                  <div className="table-wrap">
                    <div className="bench">
                      {TASK_CONTEXT.map((row) => {
                        const remaining = 100 - row.policy
                        const missesGate = row.recall < 0.9
                        return (
                          <div key={row.repo} style={{ display: 'contents' }}>
                            <div className="bench__repo">
                              {row.repo} <span className="bench__lang">{row.lang}</span>
                            </div>
                            <div className="bench__track">
                              <div className="bench__fill" style={{ width: `${Math.max(remaining, 2)}%` }} />
                            </div>
                            <div className="bench__value">−{row.policy.toFixed(1)}%</div>
                            <div className={`bench__recall${missesGate ? ' bench__recall--miss' : ''}`}>
                              {row.recall.toFixed(2)}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              )}

              {activeBenchTab === 'repos' && (
                <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
                  <h4>Representation compression — two tables, not one campaign</h4>
                  <p className="mono-note" style={{ margin: 0 }}>
                    Showing top cohort-1/4 rows. Cohort 5 is the {COHORT5_BUILDS.repos}-repo indexing gate. Adding the two tables (22+{COHORT5_BUILDS.repos}) is a
                    composite, not a single measured suite.
                  </p>
                  <div className="table-wrap">
                    <table className="table table--stack" style={stackLabels(['Language', 'Reduction', 'Coverage'])}>
                      <thead>
                        <tr>
                          <th>Repository</th>
                          <th>Language</th>
                          <th className="num">Reduction</th>
                          <th className="num">Declaration Coverage</th>
                        </tr>
                      </thead>
                      <tbody>
                        {REPRESENTATION.slice(0, 15).map((row) => (
                          <tr key={row.repo}>
                            <td>{row.repo}</td>
                            <td className="mono-note">{row.lang}</td>
                            <td className="num signal">−{row.reduction.toFixed(1)}%</td>
                            <td className="num">{row.coverage}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="mono-note">Full per-repo rows live in the published cohort tables, not as one 116-repo campaign.</p>
                </div>
              )}

              {activeBenchTab === 'langs' && (
                <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
                  <h4>Per-Language Extraction Recall (Cohort 5)</h4>
                  <div className="table-wrap">
                    <table className="table table--stack" style={stackLabels(['Repos', 'Files', 'Symbols', 'Recall vs ctags'])}>
                      <thead>
                        <tr>
                          <th>Language</th>
                          <th className="num">Repos</th>
                          <th className="num">Files</th>
                          <th className="num">Symbols</th>
                          <th className="num">Recall vs ctags</th>
                        </tr>
                      </thead>
                      <tbody>
                        {COHORT5.map((row) => (
                          <tr key={row.lang}>
                            <td>{row.lang}</td>
                            <td className="num">{row.repos}</td>
                            <td className="num">{row.files.toLocaleString()}</td>
                            <td className="num">{row.symbols.toLocaleString()}</td>
                            <td className="num">
                              {row.recall === null ? (
                                <span className="dim">no oracle</span>
                              ) : (
                                <span className={row.recall < 90 ? 'amber' : 'signal'}>{row.recall}%</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeBenchTab === 'repair' && (
                <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
                  <h4>Fase C: Mutation Bug Repair Success Rate &amp; Token Consumption</h4>
                  <div className="table-wrap">
                    <table className="table table--stack" style={stackLabels(['Bugs fixed', 'Median tokens'])}>
                      <thead>
                        <tr>
                          <th>Evaluation Baseline</th>
                          <th className="num">Bugs Fixed</th>
                          <th className="num">Median Tokens</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Baseline A · Full Source File Access</td>
                          <td className="num">{FASE_C_COHORT5.a.fixed}/{FASE_C_COHORT5.a.of}</td>
                          <td className="num">{FASE_C_COHORT5.a.medianTokens.toLocaleString()}</td>
                        </tr>
                        <tr>
                          <td>Baseline C · Archis Compact + Source on Demand</td>
                          <td className="num signal">{FASE_C_COHORT5.c.fixed}/{FASE_C_COHORT5.c.of}</td>
                          <td className="num signal">{FASE_C_COHORT5.c.medianTokens.toLocaleString()}</td>
                        </tr>
                        <tr>
                          <td>Baseline D · Pure Graph Only</td>
                          <td className="num">{FASE_C_COHORT5.d.fixed}/{FASE_C_COHORT5.d.of}</td>
                          <td className="num">{FASE_C_COHORT5.d.medianTokens.toLocaleString()}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeBenchTab === 'fidelity' && (
                <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
                  <h4>Fidelity Guarantees &amp; Verification Gates</h4>
                  <div className="table-wrap">
                    <table className="table">
                      <tbody>
                        {FIDELITY.map((row) => (
                          <tr key={row.signal}>
                            <td style={{ width: '35%' }}>{row.signal}</td>
                            <td className="dim" style={{ fontSize: '0.875rem' }}>{row.result}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <p className="mono-note">{BENCHMARK_NOTE}</p>
          </div>
        </section>

        {/* Section 6: Agent Loop */}
        <section className="shell" id="agent-loop" style={{ paddingBottom: 'var(--s8)', scrollMarginTop: '5rem' }}>
          <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
            <div className="stack" style={{ ['--gap' as string]: 'var(--s2)' }}>
              <Label index="06">End-to-End Execution</Label>
              <h2>LLM Agent Multi-Turn Campaign Analysis</h2>
              <p className="dim" style={{ maxWidth: '72ch', margin: 0, fontSize: '0.9375rem', lineHeight: 1.6 }}>
                To evaluate whether token savings translate directly into reduced financial cost and increased
                task stability, we executed paired multi-turn runs using OpenAI Codex CLI (<code>gpt-5.5</code>)
                across 8 independent trials:
              </p>
            </div>

            <div className="glass" style={{ padding: 'var(--s5) var(--s6)' }}>
              <div className="table-wrap">
                <table className="table table--stack" style={stackLabels(['Codex, native tools', 'Through Archis', 'Difference'])}>
                  <thead>
                    <tr>
                      <th style={{ paddingBottom: 'var(--s4)' }}>Measure</th>
                      <th style={{ textAlign: 'right', paddingBottom: 'var(--s4)' }}>Codex (Native Tools)</th>
                      <th style={{ textAlign: 'right', paddingBottom: 'var(--s4)' }}>Through Archis MCP</th>
                      <th style={{ textAlign: 'right', paddingBottom: 'var(--s4)' }}>Observed Delta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {AGENT_LOOP.rows.map((row) => (
                      <tr key={row.measure} className={row.win ? '' : 'dim'}>
                        <td style={{ paddingBlock: 'var(--s3)' }}>
                          {row.measure}
                          {row.separated && <span className="mono-note" style={{ marginLeft: '0.5rem' }}>(separated)</span>}
                        </td>
                        <td style={{ textAlign: 'right', paddingBlock: 'var(--s3)' }} className="dim">{row.control}</td>
                        <td style={{ textAlign: 'right', paddingBlock: 'var(--s3)', fontWeight: 600 }}>{row.chi}</td>
                        <td style={{ textAlign: 'right', paddingBlock: 'var(--s3)' }} className={row.win ? 'signal' : ''}>{row.delta}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <p className="dim" style={{ fontSize: '0.9375rem', lineHeight: 1.6, maxWidth: '72ch', margin: 0 }}>
              <strong>Key Finding:</strong> Native agents write ad-hoc AST scripts on the fly, yielding 82% run-to-run output
              variance. Archis stabilizes agent turns into bounded tool calls, reducing output variance to 5% while cutting input tokens by 46%.
            </p>
          </div>
        </section>

        {/* Section 7: Security */}
        <section className="shell" id="security" style={{ paddingBottom: 'var(--s8)', scrollMarginTop: '5rem' }}>
          <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
            <div className="stack" style={{ ['--gap' as string]: 'var(--s3)' }}>
              <Label index="07">Security &amp; Cryptography</Label>
              <h2>Cryptographic Safety, Offline Leases &amp; Zero-Leakage</h2>
              <p className="lead" style={{ maxWidth: '72ch' }}>
                Archis enforces an absolute separation between the customer's machine (semantic computation) and
                cloud control plane (licensing and updates).
              </p>
            </div>

            <PrivacyBoundary />

            <div className="grid-2" style={{ gap: 'var(--s5)' }}>
              <div className="card stack" style={{ ['--gap' as string]: 'var(--s3)', padding: 'var(--s5)' }}>
                <h4 style={{ fontFamily: 'var(--display)', fontSize: '1.15rem', margin: 0, color: 'var(--text)' }}>
                  Ed25519 Clamped Offline Leases
                </h4>
                <p className="dim" style={{ fontSize: '0.9375rem', lineHeight: 1.6, margin: 0 }}>
                  CLI licensing operates completely offline. An Ed25519-signed JSON token encodes access permissions and is
                  strictly clamped to subscription expiry deadlines. Tampered leases or mismatched machine identities
                  are rejected with distinct exit codes.
                </p>
              </div>

              <div className="card stack" style={{ ['--gap' as string]: 'var(--s3)', padding: 'var(--s5)' }}>
                <h4 style={{ fontFamily: 'var(--display)', fontSize: '1.15rem', margin: 0, color: 'var(--text)' }}>
                  Type-Enforced Telemetry Allowlist
                </h4>
                <p className="dim" style={{ fontSize: '0.9375rem', lineHeight: 1.6, margin: 0 }}>
                  Telemetry is opt-in and off by default. In the Rust CLI crate, event properties are restricted to
                  {' '}<code>&amp;'static str</code> types at compile time, ensuring file paths, variable names, code snippets,
                  and user queries can never be serialized or transmitted.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 8: Conclusion & Call to Action */}
        <section className="shell" id="conclusion" style={{ paddingBottom: 'var(--s9)' }}>
          <div className="panel panel--signal center" style={{ padding: 'var(--s7) var(--s5)' }}>
            <h2 style={{ maxWidth: '30ch', margin: '0 auto' }}>Reproduce it yourself</h2>
            <p className="dim" style={{ maxWidth: '56ch', margin: 'var(--s4) auto var(--s5)' }}>
              The per-repository results of each published run, the repositories with their pins and licences, and the
              method behind each figure are public in{' '}
              <a href={BENCHMARK_REPO.url} target="_blank" rel="noreferrer">
                <code>{BENCHMARK_REPO.name}</code>
              </a>
              ; the campaign reports stay internal, and the harness follows a release or two later. On your own code, <code>chi measure ./src</code> gives your number in about a minute.
            </p>
            <div className="row" style={{ justifyContent: 'center', gap: 'var(--s3)', flexWrap: 'wrap' }}>
              <Link to="/docs/install" className="cta cta--primary">
                Install free →
              </Link>
              <Link to="/pricing" className="cta">
                Explore Team Plans →
              </Link>
              <Link to="/verify" className="cta">
                Check it yourself →
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  )
}
